/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import styled from "styled-components";
import * as React from "react";
import { devices, fontFamilies } from "components/shared/Styles";
import {
  TrackItem,
  TrackItemProps,
} from "components/EnhancedSpeech/ProTierEnhanceSpeech/TrackItem";
import { isMobileSafari } from "utilities/BrowserUtilities";
import { SafariMobileNotice } from "components/EnhancedSpeech/ProTierEnhanceSpeech/SafariMobileNotice";

const NullTracksContainer = styled.div`
  grid-area: empty-list;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 430px;
  gap: 8px;
  box-sizing: border-box;
  position: relative;

  ${devices.small} {
    display: none;
  }
`;

const Overlay = styled.div`
  position: absolute;
  background: linear-gradient(rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.9));
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const NullTrack = styled.div`
  background-color: #f5f5f5;
  border-radius: 16px;
  height: 90px;
`;

export const NullTracks: React.FC = () => {
  return (
    <NullTracksContainer>
      {[...Array(4)].map((_, index) => (
        <NullTrack data-test-id="null-track" key={index} />
      ))}
      <Overlay />
    </NullTracksContainer>
  );
};

const TrackListWrapper = styled.div`
  grid-area: track-list;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: calc(100vh - 80px);
  overflow: hidden;
  ${devices.small} {
    min-height: fit-content;
    max-height: 100%;
    height: 100%;
  }
`;

const TrackListContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 460px;
  flex-grow: 1;
  overflow-y: auto;
  height: auto;
  gap: 8px;
  box-sizing: border-box;
  overflow: auto;
  margin: 4px 0;

  ${devices.small} {
    padding: 0 24px;
    min-height: fit-content;
    max-height: 100%;
    height: 100%;
  }
`;

const TrackListFooter = styled.span`
  text-align: center;
  font: normal normal normal 16px/20px ${fontFamilies};
  margin-top: 42px;
  margin-bottom: 48px;
  color: var(--spectrum-global-color-static-gray-800);
`;

export const TrackList: React.FC<{
  trackItems: TrackItemProps[];
}> = ({ trackItems }) => {
  if (trackItems.length > 0) {
    return (
      <TrackListWrapper>
        <TrackListContainer data-testid="track-list-container">
          {isMobileSafari() && <SafariMobileNotice />}
          {trackItems.map((item) => (
            <TrackItem key={item.id} {...item} />
          ))}
          <TrackListFooter>
            {I18n.t("enhance_speech.premium.expiration_notice")}
          </TrackListFooter>
        </TrackListContainer>
      </TrackListWrapper>
    );
  } else {
    return (
      <>
        <NullTracks />
      </>
    );
  }
};
